import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { StructureUpdatePreview } from "@/types/structure";
import { WarningAlert } from "@/components/shared/Alerts";

type StructureUpdatePreviewDialogProps = {
  preview: StructureUpdatePreview | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApply: () => void;
  isApplying: boolean;
};

export function StructureUpdatePreviewDialog({
  preview,
  open,
  onOpenChange,
  onApply,
  isApplying,
}: StructureUpdatePreviewDialogProps) {
  if (!preview) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Structure Update Preview</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <p className="text-muted-foreground">
            Previewing changes for{" "}
            <span className="font-medium text-foreground">{preview.structureName}</span>.
          </p>

          <p>{preview.message}</p>

          <div className="space-y-2">
            {preview.affectedMaskCount > 0 && (
              <WarningAlert>
                This update will affect {preview.affectedMaskCount} other mask
                {preview.affectedMaskCount !== 1 ? "s" : ""}.
              </WarningAlert>
            )}
            {preview.changes.length > 0 ? (
              preview.changes.map((change) => (
                <div key={change.field} className="rounded-md border bg-muted/30 px-3 py-2">
                  <p className="font-medium">{change.label}</p>
                  <p className="text-xs text-muted-foreground">
                    {String(change.oldValue ?? "—")} → {String(change.newValue ?? "—")}
                  </p>
                </div>
              ))
            ) : (
              <p className="text-muted-foreground">No changes detected.</p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isApplying}>
            Cancel
          </Button>
          <Button onClick={onApply} disabled={isApplying}>
            {isApplying ? "Applying..." : "Apply Update"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
